<script setup lang="ts">
import type { RouteLocationAsPathGeneric, RouteLocationAsRelativeGeneric } from 'vue-router'

export type ButtonShape = 'round' | 'square' | 'icon'
export type ButtonSize = 'small' | 'large'
export type ButtonDecoration = 'outline' | 'fill' | 'underline' | 'fill-outline'

export type ButtonProps = {
  size?: ButtonSize
  shape?: ButtonShape
  decoration?: ButtonDecoration
  isDisabled?: boolean
  href?: string
  to?: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric
  isOnDark?: boolean
}

const {
  size = 'large',
  shape = 'round',
  decoration = 'outline',
  href = '',
  to = '',
} = defineProps<ButtonProps>()
</script>

<template>
  <a
    v-if="href"
    class="vgs-button"
    :disabled="isDisabled"
    :class="[size, shape, decoration, !$slots.default && 'icon-only', isOnDark && 'on-dark']"
    :href="href"
  >
    <slot />
  </a>
  <component
    :is="to ? 'router-link' : 'button'"
    v-else
    class="vgs-button"
    :disabled="isDisabled"
    :class="[size, shape, decoration, !$slots.default && 'icon-only', isOnDark && 'on-dark']"
    :to="to"
    v-bind="$attrs"
  >
    <slot />
  </component>
</template>

<style scoped lang="scss">
  a,
  button {
    all: unset;
  }

  .vgs-button {
    --btn-color: var(--color-blue-50);
    --btn-color-on-dark: var(--color-yellow-10);
    --btn-bgcolor: transparent;
    --btn-bgcolor-hover: rgb(0 0 0 / 7.5%);
    --btn-bgcolor-hover-on-dark: rgb(255 255 255 / 10%);
    --btn-bgcolor-disabled: var(--color-yellow-10);
    --btn-color-fill: var(--color-yellow-10);
    --btn-bgcolor-fill: var(--color-blue-50);
    --btn-bgcolor-fill-hover: var(--color-blue-60);
    --btn-bgcolor-fill-hover-on-dark: var(--color-yellow-20);
    --btn-bgcolor-fill-outline: var(--color-white);
    --btn-border-color-fill-outline: var(--color-gray-20);
    --btn-bgcolor-fill-outline-hover: var(--color-gray-10);

    background-color: var(--btn-bgcolor);
    border: 2px solid currentcolor;
    border-radius: 60px;
    cursor: pointer;
    color: var(--btn-color);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.25rem;
    user-select: none;
    white-space: nowrap;
    width: fit-content;
    transition: background-color .25s, border-color .25s;

    &.on-dark {
      color: var(--btn-color-on-dark);

      &:hover,
      &:active {
        &:not(:disabled, .underline) {
          background-color: var(--btn-bgcolor-hover-on-dark);
        }
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: .5;
    }

    &:hover,
    &:active {
      &:not(:disabled, .underline) {
        background-color: var(--btn-bgcolor-hover);
      }
    }

    &.icon {
      padding: 9px;
      font-size: 1.375rem;
      height: 1em;
      width: 1em;
      display: flex;
      place-content: center;
    }

    &.small {
      font-size: 1rem;
      padding: 6px 16px;

      :deep(svg) {
        font-size: 1.125rem;
      }

      &.icon {
        padding: 8px;
        font-size: 1rem;
      }
    }

    &.fill {
      color: var(--btn-color-fill);
      border-color: var(--btn-bgcolor-fill);
      background-color: var(--btn-bgcolor-fill);

      &.on-dark {
        color: var(--btn-bgcolor-fill);
        border-color: var(--btn-color-fill);
        background-color: var(--btn-color-fill);

        &:hover,
        &:active {
          &:not(:disabled) {
            border-color: var(--btn-bgcolor-fill-hover-on-dark);
            background-color: var(--btn-bgcolor-fill-hover-on-dark);
          }
        }
      }

      &:disabled {
        background-color: var(--btn-bgcolor-disabled);
        border-color: var(--btn-bgcolor-disabled);
      }

      &:hover,
      &:active {
        &:not(:disabled) {
          border-color: var(--btn-bgcolor-fill-hover);
          background-color: var(--btn-bgcolor-fill-hover);
        }
      }
    }

    &.fill-outline {
      background-color: var(--btn-bgcolor-fill-outline);
      border-color: var(--btn-border-color-fill-outline);

      &:disabled {
        background-color: var(--btn-bgcolor-disabled);
        border-color: var(--btn-bgcolor-disabled);
      }

      &:hover,
      &:active {
        &:not(:disabled) {
          background-color: var(--btn-bgcolor-fill-outline-hover);
        }
      }
    }

    &.underline {
      border: none;
      border-bottom: 2px solid currentcolor;
      border-radius: 0;
      display: inline-flex;
      padding: 0;
      padding-bottom: 4px;
      margin: 9px 8px;

      &.small {
        margin: 4px;
        padding-bottom: 2px;
      }

      &:hover,
      &:active {
        &:not(:disabled) {
          border-color: transparent;
        }
      }
    }

    &.square {
      border-radius: 0;
    }

    @include visible-focus-ring;
  }
</style>
